import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/********************************************************************************
 * Copyright (c) 2022 Precies. Software and others
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License v. 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 ********************************************************************************/
import { useContext, useEffect, useState } from 'react';
import { Box, Link, TextField, Typography, Button, InputAdornment, Select, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { MainContext } from '../../context';
import { DelayedLoadIndicator } from '../delayed-load-indicator';
import { styled } from '@mui/material/styles';
import { SaveButton } from '../save-button';
import { DetailRow, DetailsCard, DetailsGroupLabel } from '../details-card';
import { useSavedFlash } from '../../hooks/use-saved-flash';
import { SectionTitleRow, SettingsSectionTitle } from '../../pages/user/settings/settings-primitives';
import { useNamespaceDetails, useUpdateNamespaceDetails } from './use-namespace-details';
// One field (or field group) per row, padded like a DetailRow so editing lines
// up with the read-only preview it replaces.
const FormRow = styled(Box)({
    padding: '0.875rem 1.125rem'
});
/** Last path segment of a URL, tolerating a trailing slash: the social account handle. */
const lastPathSegment = (url) => {
    const trimmed = url.endsWith('/') ? url.slice(0, -1) : url;
    return trimmed.substring(trimmed.lastIndexOf('/') + 1);
};
/** Splits a LinkedIn URL into the handle and the account type ('in' or 'company'). */
const parseLinkedInUrl = (url) => {
    const path = (url.endsWith('/') ? url.slice(0, -1) : url).split('/');
    return { handle: path[path.length - 1], accountType: path[path.length - 2] };
};
/** Placeholder shown for a details field the namespace hasn't filled in yet. */
const NOT_SET = '—';
/** Deep equality that treats empty and missing values alike, at every level. */
const equalIgnoringEmpty = (a, b) => {
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') {
        return (a || undefined) === (b || undefined);
    }
    const x = a;
    const y = b;
    return [...new Set([...Object.keys(x), ...Object.keys(y)])].every(key => equalIgnoringEmpty(x[key], y[key]));
};
export const NamespaceDetailsForm = props => {
    const INPUT_DISPLAY_NAME = 'display-name';
    const INPUT_DESCRIPTION = 'description';
    const INPUT_WEBSITE = 'website';
    const INPUT_SUPPORT_LINK = 'support-link';
    const INPUT_LINKEDIN = 'linkedin';
    const INPUT_GITHUB = 'github';
    const INPUT_TWITTER = 'twitter';
    const LINKED_IN_PERSONAL = 'in';
    const LINKED_IN_COMPANY = 'company';
    const context = useContext(MainContext);
    const detailsQuery = useNamespaceDetails(props.namespace.name);
    const updateDetails = useUpdateNamespaceDetails();
    const [currentDetails, setCurrentDetails] = useState();
    const [newDetails, setNewDetails] = useState();
    const [editMode, setEditMode] = useState(false);
    const { saved, flash } = useSavedFlash(1200, () => setEditMode(false));
    const [linkedInAccountType, setLinkedInAccountType] = useState(LINKED_IN_PERSONAL);
    const [savedLinkedInAccountType, setSavedLinkedInAccountType] = useState(LINKED_IN_PERSONAL);
    const copy = (arg) => {
        return JSON.parse(JSON.stringify(arg));
    };
    // Seed the editable copies from the fetched details. The social URLs are
    // reduced to handles for the form; all transforms work on copies so the
    // cached query data stays untouched.
    const seedFromDetails = (fetched) => {
        const details = copy(fetched);
        const { linkedin, github, twitter } = details.socialLinks;
        let linkedInAccountType = LINKED_IN_PERSONAL;
        if (linkedin) {
            const parsed = parseLinkedInUrl(linkedin);
            details.socialLinks.linkedin = parsed.handle;
            linkedInAccountType = parsed.accountType;
        }
        if (github) {
            details.socialLinks.github = lastPathSegment(github);
        }
        if (twitter) {
            details.socialLinks.twitter = lastPathSegment(twitter);
        }
        setCurrentDetails(copy(details));
        setNewDetails(copy(details));
        setLinkedInAccountType(linkedInAccountType);
        setSavedLinkedInAccountType(linkedInAccountType);
    };
    const fetchedDetails = detailsQuery.data;
    useEffect(() => {
        if (fetchedDetails) {
            seedFromDetails(fetchedDetails);
        }
    }, [fetchedDetails]);
    useEffect(() => {
        setEditMode(false);
    }, [props.namespace.name]);
    // Only the editable fields — echoing server-computed data back (extensions,
    // logoBytes) breaks the update endpoint. Social handles expand to full URLs.
    const buildDetailsPayload = (source, accountType) => ({
        name: source.name,
        displayName: source.displayName,
        description: source.description,
        website: source.website,
        supportLink: source.supportLink,
        socialLinks: {
            linkedin: source.socialLinks.linkedin
                ? `https://www.linkedin.com/${accountType}/${source.socialLinks.linkedin}`
                : undefined,
            github: source.socialLinks.github ? 'https://github.com/' + source.socialLinks.github : undefined,
            twitter: source.socialLinks.twitter ? 'https://twitter.com/' + source.socialLinks.twitter : undefined
        },
        logo: source.logo
    });
    // Compared as payloads, so only what Save would send counts as a change.
    const noChanges = !currentDetails ||
        !newDetails ||
        equalIgnoringEmpty(buildDetailsPayload(currentDetails, savedLinkedInAccountType), buildDetailsPayload(newDetails, linkedInAccountType));
    const saveDetails = async () => {
        if (!newDetails) {
            return;
        }
        try {
            await updateDetails.mutateAsync({
                detailsUrl: props.namespace.detailsUrl,
                details: buildDetailsPayload(newDetails, linkedInAccountType)
            });
            setCurrentDetails(copy(newDetails));
            setSavedLinkedInAccountType(linkedInAccountType);
            flash();
        }
        catch (err) {
            context.handleError(err);
        }
    };
    const handleInputChange = (event) => {
        if (!newDetails) {
            return;
        }
        const input = event.target;
        const details = copy(newDetails);
        switch (input.name) {
            case INPUT_DISPLAY_NAME:
                details.displayName = input.value;
                break;
            case INPUT_DESCRIPTION:
                details.description = input.value;
                break;
            case INPUT_WEBSITE:
                details.website = input.value;
                break;
            case INPUT_SUPPORT_LINK:
                details.supportLink = input.value;
                break;
            // Pasting a full profile URL reduces it to the handle.
            case INPUT_LINKEDIN:
                if (input.value.startsWith('https://www.linkedin.com/')) {
                    const { handle, accountType } = parseLinkedInUrl(input.value);
                    details.socialLinks.linkedin = handle;
                    setLinkedInAccountType(accountType);
                }
                else {
                    details.socialLinks.linkedin = input.value;
                }
                break;
            case INPUT_GITHUB:
                details.socialLinks.github = input.value.startsWith('https://github.com/')
                    ? lastPathSegment(input.value)
                    : input.value;
                break;
            case INPUT_TWITTER:
                details.socialLinks.twitter = input.value.startsWith('https://twitter.com/')
                    ? lastPathSegment(input.value)
                    : input.value;
                break;
        }
        setNewDetails(details);
    };
    const handleSelectChange = (event) => setLinkedInAccountType(event.target.value);
    // Discard field edits by re-seeding from the cached details.
    const cancelEditing = () => {
        if (fetchedDetails) {
            seedFromDetails(fetchedDetails);
        }
        setEditMode(false);
    };
    const canEdit = Boolean(props.namespace.detailsUrl);
    // The server 500s on relative URLs, so only absolute ones may be submitted.
    const isAbsoluteUrl = (value, allowMailto = false) => {
        if (!value) {
            return true;
        }
        try {
            const protocol = new URL(value).protocol;
            return protocol === 'http:' || protocol === 'https:' || (allowMailto && protocol === 'mailto:');
        }
        catch {
            return false;
        }
    };
    // Social links are sent as https://<site>/<handle>, so a handle must be a single path segment.
    const isValidHandle = (value) => !value || /^[^\s/]+$/.test(value);
    const websiteValid = isAbsoluteUrl(newDetails?.website);
    const supportLinkValid = isAbsoluteUrl(newDetails?.supportLink, true);
    const linkedinValid = isValidHandle(newDetails?.socialLinks.linkedin);
    const githubValid = isValidHandle(newDetails?.socialLinks.github);
    const twitterValid = isValidHandle(newDetails?.socialLinks.twitter);
    const formValid = websiteValid && supportLinkValid && linkedinValid && githubValid && twitterValid;
    // Read-only notice shown instead of the Edit affordance; for unverified
    // namespaces the "Claim ownership" phrase links to the claiming docs.
    const renderReadOnlyNotice = () => {
        const claimUrl = context.pageSettings.urls.namespaceAccessInfo;
        return (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '0.625rem', mb: '1.25rem' }, children: [props.namespace.verified ? (_jsx(LockOutlinedIcon, { sx: { fontSize: '1rem', color: 'text.disabled', flexShrink: 0 } })) : (_jsx(WarningAmberIcon, { sx: { fontSize: '1rem', color: 'warningAccent', flexShrink: 0 } })), _jsx(Typography, { sx: { fontSize: '0.8125rem', color: 'text.disabled', lineHeight: 1.5 }, children: props.namespace.verified ? ('Only namespace owners can edit these details.') : (_jsxs(_Fragment, { children: ["This namespace isn't verified yet.", ' ', claimUrl ? (_jsx(Link, { color: 'secondary', underline: 'hover', target: '_blank', href: claimUrl, sx: { fontWeight: 600 }, children: "Claim ownership" })) : ('Claim ownership'), ' ', "to verify it and unlock editing its details."] })) })] }));
    };
    // Read-only view shown by default; the form replaces it while editing.
    // Every field shows up even when empty — that's what makes it obvious how
    // much filling in a claimed namespace unlocks.
    const renderLinkRow = (label, url) => (_jsx(DetailRow, { label: label, empty: !url, children: url ? (_jsx(Link, { color: 'secondary', underline: 'hover', target: '_blank', href: url, children: url.replace(/https?:\/\//, '') })) : (NOT_SET) }, label));
    const renderHandleRow = (label, handle) => (_jsx(DetailRow, { label: label, empty: !handle, children: handle || NOT_SET }, label));
    const renderPreview = (details) => (_jsxs(DetailsCard, { children: [_jsx(DetailRow, { label: 'Display name', children: details.displayName || details.name }), _jsx(DetailRow, { label: 'Description', empty: !details.description, children: details.description || NOT_SET }), _jsx(DetailsGroupLabel, { children: "Links" }), renderLinkRow('Website', details.website), renderLinkRow('Support', details.supportLink), _jsx(DetailsGroupLabel, { children: "Social accounts" }), renderHandleRow('LinkedIn', details.socialLinks.linkedin), renderHandleRow('GitHub', details.socialLinks.github), renderHandleRow('Twitter / X', details.socialLinks.twitter)] }));
    const renderForm = (details) => (_jsxs(DetailsCard, { children: [_jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'Display name', name: INPUT_DISPLAY_NAME, value: details.displayName ?? '', onChange: handleInputChange, inputProps: { maxLength: 32 } }) }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', multiline: true, minRows: 3, maxRows: 8, label: 'Description', placeholder: 'What is this namespace about?', name: INPUT_DESCRIPTION, value: details.description ?? '', onChange: handleInputChange }) }), _jsx(DetailsGroupLabel, { children: "Links" }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'Website', type: 'url', placeholder: 'https://example.com', name: INPUT_WEBSITE, value: details.website ?? '', onChange: handleInputChange, error: !websiteValid, helperText: !websiteValid ? 'Enter a full URL, including https://' : undefined }) }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'Support link', type: 'url', placeholder: 'https:// or mailto:', name: INPUT_SUPPORT_LINK, value: details.supportLink ?? '', onChange: handleInputChange, error: !supportLinkValid, helperText: !supportLinkValid ? 'Enter a full URL, including https:// or mailto:' : undefined }) }), _jsx(DetailsGroupLabel, { children: "Social accounts" }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'LinkedIn', placeholder: 'account name', name: INPUT_LINKEDIN, value: details.socialLinks.linkedin ?? '', onChange: handleInputChange, error: !linkedinValid, helperText: !linkedinValid ? 'Just the account name — no slashes or spaces' : undefined, InputProps: {
                        startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsxs(Select, { variant: 'standard', disableUnderline: true, value: linkedInAccountType, onChange: handleSelectChange, sx: {
                                    fontSize: '0.875rem',
                                    color: 'text.disabled',
                                    '& .MuiSelect-select': { py: 0 }
                                }, children: [_jsx(MenuItem, { value: LINKED_IN_PERSONAL, children: "linkedin.com/in/" }), _jsx(MenuItem, { value: LINKED_IN_COMPANY, children: "linkedin.com/company/" })] }) }))
                    } }) }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'GitHub', placeholder: 'username', name: INPUT_GITHUB, value: details.socialLinks.github ?? '', onChange: handleInputChange, error: !githubValid, helperText: !githubValid ? 'Just the account name — no slashes or spaces' : undefined, InputProps: {
                        startAdornment: _jsx(InputAdornment, { position: 'start', children: "github.com/" })
                    } }) }), _jsx(FormRow, { children: _jsx(TextField, { fullWidth: true, size: 'small', label: 'Twitter / X', placeholder: 'username', name: INPUT_TWITTER, value: details.socialLinks.twitter ?? '', onChange: handleInputChange, error: !twitterValid, helperText: !twitterValid ? 'Just the account name — no slashes or spaces' : undefined, InputProps: {
                        startAdornment: _jsx(InputAdornment, { position: 'start', children: "twitter.com/" })
                    } }) }), _jsxs(FormRow, { sx: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }, children: [_jsx(Button, { onClick: cancelEditing, children: "Cancel" }), _jsx(SaveButton, { color: 'secondary', saved: saved, disabled: noChanges || !formValid || updateDetails.isPending, onClick: saveDetails, children: "Save namespace details" })] })] }));
    if (!newDetails) {
        return _jsx(DelayedLoadIndicator, { loading: detailsQuery.isLoading });
    }
    return (_jsxs(_Fragment, { children: [_jsxs(SectionTitleRow, { sx: { minHeight: '2.375rem' }, children: [_jsx(SettingsSectionTitle, { component: 'h3', sx: { m: 0 }, children: "Details" }), canEdit && !editMode ? (_jsx(Button, { variant: 'outlined', startIcon: _jsx(EditIcon, { sx: { fontSize: '0.875rem' } }), onClick: () => setEditMode(true), children: "Edit" })) : null] }), !canEdit ? renderReadOnlyNotice() : null, editMode ? renderForm(newDetails) : renderPreview(newDetails)] }));
};
//# sourceMappingURL=namespace-details-form.js.map